import RADApplication from '@/Management/RADApplication';
import ResourceManager from '@/Management/ResourceManager';
import ComponentManager from '@/Management/ComponentManager';
import { getType } from '@/Utility/TypeInfo';
import Utilities from '@/Utility/Utilities';
import Messanger from '@/Utility/Messenger';
import type {DisposeBehavior, Handler} from '#/DefaultViewExtensionInterfaces';
import type MessageT from '@/Messages/Message';
import type MessageBoxViewModelT from '@/ViewModels/Controls/MessageBoxViewModel';
import MessageBoxViewModelBuilder from '@/Builder/MessageBoxViewModelBuilder';
import { DataType, IClass } from '#/CoreFrameInterfaces';
import CallbackBatcher from '@/Utility/CallbackBatcher';
import PromiseBatcher from '@/Utility/PromiseBatcher';

const DEBUG_MODE: boolean = false;
const IS_DISPOSING: string = "__po_@disposing";
export default class Class implements IClass {    static register: (path: string) => void;

    private __isDisposing = false;
    
    protected blacklist: Array<string>;

    /**
     * Gibt an, ob die Instanz disposed ist
     */
    public isDisposed: boolean;

    /**
     * Gibt an, ob die Instanz disposed werden kann
     */
    public isDisposable: boolean;

    /**
     * Gibt an, ob die Properties der Instanz disposed werden können
     */
    public disposeProperties: boolean;

    /**
     * Gibt an, welche Properties der Instanz nicht disposed werden können
     */
    public undisposeProperties: Array<string>;

    private equalityProperties: Array<string>;

    /**
     * Setzt den Typnamen für das Control
     */
    public type: string;

    /**
     * Der Unique-Identifier des Controls
     */
    public identifier: string;

    /**
     * Beinhaltet alle Subscriptions dieser Instanz
     */
    public subscriptionCollection: Array<KnockoutSubscription>;

    private disposables: Class[];

    /**
     * This dictionary-like object holds JQueryDeferreds-by-TimerIds.
     * The timerIds refer to the return-value of the async-function (below).
     * The Deferreds are resolved when executing the callback of the async-function is done.
     */
    public triggers: { [key: number]: JQueryDeferred<void> };

    private static ignoreShortTransactionLockGroupError: boolean = false;

    private static currentPromiseBatcher: PromiseBatcher<boolean>;
    
    public constructor() {

        this.isDisposed = false;
        this.isDisposable = false;
        this.disposeProperties = true;
        this.blacklist = ['blacklist', 'type', 'identifier', 'isDisposed', '__isDisposing', 'dispose', 'isDisposable', 'disposeProperties', 'undisposeProperties', 'removeProperty', 'disposeObject', 'validateStatus'];
        this.undisposeProperties = ['type', 'identifier', 'isDisposed', 'isDisposable', '_dispose', 'removeProperty', 'disposeProperties', 'undisposeProperties', 'validateStatus', 'subscriptionCollection'];
        this.triggers = {};
        this.type = getType(this);
        this.identifier = _.uniqueId(this.type);
        this.subscriptionCollection = [];
        this.disposables = [];

        if (RADApplication.getInstance().profiler != null) {
            RADApplication.getInstance().profiler.onInstanceCreated(this);
        }
    }

    /**
     * Gibt den übergebenen Parameter oder einen Default Wert zurück
     */
    public paramOrDefault<T>(param: T, defaultValue?: T | (() => T), parsingFunc?: (value: any, defaultValue: T | (() => T)) => T): T {
        if (param != null && parsingFunc) {
            return parsingFunc(param, defaultValue);
        }

        if (param != null) {
            return param;
        }

        if (_.isFunction(defaultValue)) {
            return (defaultValue as Function)();
        }

        return defaultValue;
    }

    /**
     * Validiert den Status von DTO-Objekten
     * @param showMessageBox default True
     */
    public validateStatus(dto: any, showMessageBox: boolean = true, disposedCheck = true): boolean {
        if ((disposedCheck && this.isDisposedOrDisposing()) || !dto) {
            return false;
        }

        let result = true;
        let MessageBoxViewModel = ComponentManager.getViewModel<typeof MessageBoxViewModelT>('MessageBoxViewModel');

        const app = RADApplication.getInstance();
        switch (dto.status) {
            case 'Warning':
                if (showMessageBox) {
                    app.showMessageBox(MessageBoxViewModel.createWarningMessageBox(dto.errorMessage));
                }

                result = false;
                break;

            case 'Error':
                if (showMessageBox) {
                    app.showMessageBox(MessageBoxViewModel.createWarningMessageBox(dto.errorMessage));
                }

                result = false;
                break;

            case 'Exception':
                if (showMessageBox) {
                    const messageBox = new MessageBoxViewModelBuilder()
                        .initForExceptionInfoMessageBox(dto.errorInformation, dto.errorMessage)
                        .build();
                    app.showMessageBox(messageBox);
                }

                result = false;
                break;

            case 'NoLegal':
                if (showMessageBox) {
                    if (dto.errorMessage && dto.errorMessage.indexOf('RADFoundation.ProblemDomain.Rights.SecurityException') == -1) {
                        app.showMessageBox(MessageBoxViewModel.createWarningMessageBox(dto.errorMessage));
                    } else {
                        app.showMessageBox(MessageBoxViewModel.createWarningMessageBox(ResourceManager.getInstance().get('SECURITY_EXCEPTION_ERROR')));
                    }
                }

                result = false;
                break;

            case 'SaveRequired':
                if (showMessageBox) {
                    app.showMessageBox(MessageBoxViewModel.createWarningMessageBox(dto.errorMessage));
                }

                result = false;
                break;

            case 'IsLockedByOther':
                if (showMessageBox) {
                    if (dto.lockedBy) {
                        app.showMessageBox(MessageBoxViewModel.createLockedByUserMessageBox(dto.lockedBy, dto.lockedObjectIdent));
                    } else {
                        app.showMessageBox(MessageBoxViewModel.createWarningMessageBox(dto.errorMessage));
                    }
                }

                result = false;
                break;

            case 'ADOException':
                app.handleDatabaseException();
                result = false;
                break;

            case 'ShortTransactionLockGroupError':
                this.handleShortTransactionLockGroupError(MessageBoxViewModel);
                result = false;
                break;
        }

        if (dto.lockedByUser != null && showMessageBox) {
            let objectIdent = dto.lockedObjectIdent;
            if (dto.subject.isSingleton)
                objectIdent = ResourceManager.getInstance().get(`${dto.subject.pdTypeName}_ERGNAME`);
            app.showMessageBox(MessageBoxViewModel.createLockedByUserMessageBox(dto.lockedByUser, objectIdent));
        }

        return result;
    }

    private handleShortTransactionLockGroupError(MessageBoxViewModel: typeof MessageBoxViewModelT): void {
        if (Class.ignoreShortTransactionLockGroupError)
            return;

        Class.ignoreShortTransactionLockGroupError = true;
        const boxVm = MessageBoxViewModel.createWarningMessageBox(
            ResourceManager.getInstance().get('EXCLUSIVE_LOCK_IS_TAKEN_MESSAGE'),
            () => {
                Class.ignoreShortTransactionLockGroupError = false;
                var currentDvc = RADApplication.getInstance().currentDetailViewContext();
                currentDvc?.cancel();
            });

        RADApplication.getInstance().showMessageBox(boxVm);
    }

    /** 
     * Definiert eine asynchrone Ausführung unter Berücksichtigung des Disposing-Mechanismus.
     * Returns the timerId returned from window.setTimeout, which is used in the triggers-dictionary (above).
     */
    public async(func: Function, timeout?: number): number {
        if (_.isFunction(func)) {
            let dfd = $.Deferred();
            let self = this;
            let timer = window.setTimeout(function () {
                if (self.isDisposed === false) {
                    func.apply(self, arguments);
                    dfd.resolve();
                    clearTimeout(timer);
                }
            }, timeout);

            this.triggers[timer] = dfd;

            return timer;
        }

        return -1;
    }

    /**
     * Wandelt einen übergebenen Parameter in ein Observable um oder liefert ein Observable mit dem Default-Wert
     */
    public observableOrDefault<T>(value: ObservableOrDefault<T> | KnockoutObservable<T>, defaultValue?: ObservableOrDefault<T>, parsingFunc?: (value: any, defaultValue: ObservableOrDefault<T>) => T): KnockoutObservable<T> {
        if (value != null) {
            if (_.isFunction(value['done'])) {
                const observable = this.defineAsyncComputed(() => {
                    return value;
                }, defaultValue);

                return observable as any;
            } else if (ko.isObservable(value)) {
                if (ko.isComputed(value)) {
                    return value.extend({ overwritable: true });
                } else {
                    return value;
                }
            }
            else {
                if (parsingFunc) {
                    value = parsingFunc(value, defaultValue);
                }

                if (_.isArray(value)) {
                    return ko.observableArray(value) as KnockoutObservable<any>;
                } else if (_.isFunction(value)) {
                    return ko.computed(value as any);
                } else {
                    return ko.observable(value);
                }
            }
        }
        else {
            if (_.isArray(defaultValue)) {
                return ko.observableArray(defaultValue) as KnockoutObservable<any>;
            } else if (_.isFunction(defaultValue)) {
                return ko.computed(defaultValue as any);
            } else {
                return ko.observable(defaultValue);
            }
        }
    }

    /**
     * Evaluiert den Rückgabewert einer Funktion
     */
    public evaluate<T>(value: any): JQueryDeferred<T> {
        let deferred = $.Deferred();

        if (value && _.isFunction(value.done)) {
            value.done((value) => {
                deferred.resolve(value);
            });
        } else {
            deferred.resolve(value);
        }

        return deferred;
    }

    /**
     * Erzeugt einen Default-Wert von einem Typ
     */
    public defaultFromType(type: DataType): any {
        switch (type) {
            case 'string': case 'String':
                return '';

            case 'bool': case 'Boolean':
                return false;

            case 'int': case 'Integer': case 'long': case 'float': case 'double':
                return 0;

            case 'array':
                return [];

            case 'object': case 'dictionary':
                return {};

            default:
                return null;
        }
    }

    /**
     * Gibt den übergebenen Handler oder eine leere Funktion zurück
     */
    public handlerOrDefault<T extends ((...args: any[]) => any) | Function>(func: T, defaultValue?: T): T & { isEmpty?: boolean } {
        if (!Utilities.isNullOrUndefined(func)) {
            return func;
        } else if (!Utilities.isNullOrUndefined(defaultValue)) {
            return defaultValue;
        } else {
            let emptyFunc = (() => {}) as T;
            emptyFunc["isEmpty"] = true;

            return emptyFunc;
        }
    }

    private synchronizeComputed(computed, sync) {
        if (sync == null || !ko.isObservable(sync) || this.isDisposed) {
            return;
        }

        if (ko.isWriteableObservable(computed)) {
            // Als erstes den Wert in das Ziel-computed schreiben
            // Grund: Diese Methode wird oft benutzt, um ein neues Computed mit einem GenericObject-Observable (sync) in Verbindung zu bringen
            //        Falls nun computed aus irgendeinem Grund nach dem Schreiben des Wertes beim Lesen nicht den gleichen Wert wieder zurückgibt,
            //        so soll das GenericObject davon nicht direkt beeinflusst werden.
            //        Grundsätzlich könnte man auch voraussetzen, dass computed(sync()) == sync() ist und daher ein _vorhergehendes_ subscribe unnötig ist.

            computed(sync());

            // Zudem weitere Änderungen an sync ab jetzt ebenfalls an Ziel-computed weiterleiten
            let syncSub = sync.subscribe((value) => {
                var current = computed();

                if (value !== current) {
                    computed(value);
                }
            });
            this.subscriptionCollection.push(syncSub);

        }

        if (ko.isWriteableObservable(sync)) {
            // Änderungen am Ziel-Computed zurück an sync schicken
            let compSub = computed.subscribe((value) => {
                let current = sync();

                if (value !== current) {
                    sync(value);
                }
            });
            this.subscriptionCollection.push(compSub);
        }
    }

    /**
     * Definiert ein neues Computed-Property und registriert es standardmäßig in der subscriptionCollection
     */
    public defineComputed<T>(funcOrDefine: (() => T) | KnockoutComputedDefine<T>, sync?: KnockoutObservable<any>, addToSubscriptionCollection: boolean = true): KnockoutComputed<T> {
        if (this.isDisposed)
            return null;

        let computed = typeof funcOrDefine === 'function' ? ko.computed(funcOrDefine) : ko.computed(funcOrDefine);

        if (addToSubscriptionCollection)
            this.subscriptionCollection.push(computed);

        this.synchronizeComputed(computed, sync);

        return computed;
    }


    public defineAsyncComputed<T>(readFunc: () => T, initialValue?: T, suppressInitialExecution?: boolean): KnockoutAsyncComputed<T> {
        if (this.isDisposed)
            return null;

        const computed =  ko.asyncComputed<T>(readFunc, initialValue, suppressInitialExecution);

        this.subscriptionCollection.push(computed);
        
        return computed;
    }

    /**
     * Registriert ein Computed in der computedCollection
     */
    public addComputed(computed: KnockoutComputed<any>): void {
        if (this.isDisposed)
            return;

        if (computed && this.subscriptionCollection.indexOf(computed) == -1) {
            this.subscriptionCollection.push(computed);
        }
    }
    
    /**
     * Definiert ein neues PureComputed-Property und registriert es in der computedCollection
     */
    public definePureComputed(obj: any, sync?: KnockoutObservable<any>): KnockoutComputed<any> {
        if (this.isDisposed)
            return null;

        let computed = ko.pureComputed(obj);
        this.subscriptionCollection.push(computed);

        this.synchronizeComputed(computed, sync);

        return computed;
    }

    /**
     * Definiert ein neues Subscription-Property und registriert es in der subscriptionCollection.
     * Gibt als die Funktion zurück, die auch durch die Subscription aufgerufen wird (dies erlaubt
     * die Schreibweise this.doIt = this.defineSubscription(observable, function(val) { ... });
     * mit späterem Aufruf this.doIt(customValue);
     */
    public defineSubscription<T>(observable: KnockoutObservable<T>, func: (widget: T) => void, disposeImmediately?: boolean, eventType?: any): any {
        if (func == null || this.isDisposed)
            return null;

        if (eventType == undefined)
            eventType = 'change';

        const subscription = observable.subscribe((value) => {
            if (disposeImmediately === true) {
                subscription.dispose();

                if (!this.isDisposed) {
                    const idx = this.subscriptionCollection.indexOf(subscription);
                    if (idx > -1) {
                        this.subscriptionCollection.splice(idx, 1);
                    }
                }
            }

            func(value);
        }, eventType);

        this.subscriptionCollection.push(subscription);

        return func;
    }

    /**
     * Definiert eine Subscription, die so lange bestehen bleibt, bis die Kondition erreicht wurde.
     * Das automatische Disposing der Subscription ist selbst bei nicht-Eintritt der Kondition behandelt.
     * @param obs Das Observable, das beobachtet wird.
     * @param condition Die Kondition, die gelten muss, damit die Subscription abgeräumt wird.
     */
    public subscribeUntil<T>(obs: KnockoutObservable<T>, condition: (val: T) => boolean | Promise<boolean>) {
        if (this.isDisposed)
            return;

        const subscription = obs.subscribe((value: T) => {
            Promise.resolve(condition(value)).then(val => {
                if (!val)
                    return;

                subscription.dispose();

                if (this.isDisposed)
                    return;

                const index = this.subscriptionCollection.indexOf(subscription);
                this.subscriptionCollection.splice(index, 1);
            });
        });
        this.subscriptionCollection.push(subscription);
    }

    /**
     * Erzeugt einen Operationsparameter
     */
    public buildOperationParameter(dotNetTypeName: string, value: any): string {
        return 'DotNetTypeName:' + dotNetTypeName + ';Value:' + value;
    }

    /**
     * Liefert ein vergleichbares Objekt der aktuellen Instanz
     */
    public equalityComparer(): any {
        if (this.equalityProperties == null) {
            return null;
        }

        let comparable = {};
        let properties = this.equalityProperties.concat(['type']);

        for (let i = 0; i < properties.length; i++) {
            let propValue = this[properties[i]];

            if (_.isArray(propValue)) {
                let newProp = [];

                for (let j = 0; j < propValue.length; j++) {
                    if (propValue[j] && propValue[j].equalityComparer != null) {
                        newProp[j] = propValue[j].equalityComparer();
                    }
                }

                comparable[properties[i]] = newProp;
            } else {
                let newProp = ko.toJS(propValue);

                if (newProp && newProp["equalityComparer"] != null) {
                    newProp = newProp["equalityComparer"]();
                }

                comparable[properties[i]] = newProp;
            }
        }

        return comparable;
    }

    //#region MESSENGER
    private __registeredMessageIds: Array<string> = [];
    /**
     * Registriert das Objekt für eine Message. Diese wird vom Disposing automatisch deregistriert.
     * @param messageId ID der Message
     * @param callback Wird aufgerufen, bei dem empfangen einer Message
     * @param receiveLastMessageImmediately Wenn TRUE, wird die zuletzt gesendete Message direkt empfangen, auch wenn diese bereits vor dem registrieren gesendet wurde
     */
    public registerForMessageId<T extends MessageT>(messageId: string, callback: (msg: T) => void, receiveLastMessageImmediately: boolean = false) {
        if (this.__registeredMessageIds.indexOf(messageId) < 0) {
            this.__registeredMessageIds.push(messageId);
        }

        if (receiveLastMessageImmediately) {
            Messanger.registerForMessageIdAndReceive(messageId, callback, this);
        }
        else {
            Messanger.registerForMessageId(messageId, callback, this);
        }
    }

    /**
     * Deregistriert sich für eine bestimmte MessageId
     * @param messageId ID der Message
     * @param callback OPTIONAL: Wenn nicht angegeben werden alle Callbacks dieser MessageId entfernt
     */
    public unregisterForMessageId<T extends MessageT>(messageId: string, callback: (msg: T) => void = null) {
        let firstIndex = this.__registeredMessageIds.indexOf(messageId);
        if (firstIndex >= 0) {
            this.__registeredMessageIds.splice(firstIndex, 1);
        }
        Messanger.unregisterForMessageId(messageId, callback, this);
    }

    private unregisterAllMessageIds() {
        let ids = _.uniq(this.__registeredMessageIds);
        for (const id of ids) {
            this.unregisterForMessageId(id);
        }
        this.__registeredMessageIds = [];
    }
    //#endregion


    /**
     * Disposed ein beliebiges Objekt
     */
    protected disposeObject(value: any, disposePlain?: boolean): void {
        if (value == null) {
            return;
        }

        if (_.isArray(value)) {
            for (let i = 0; i < value.length; i++) {
                this.disposeObject(value[i]);
            }
            return;
        }

        if (ko.isObservable(value)) {
            try {
                let realValue = value.peek();
                this.disposeObject(realValue);
            }
            catch (e) {
                if (DEBUG_MODE)
                    console.debug("Error while Disposing KO-Observable Value: ", JSON.stringify(value), " Error: ", e.message);
            }

            for (const event of Object.keys(value._subscriptions)) {
                const subs = value._subscriptions[event];
                for (const sub of subs) {
                    sub.dispose();
                }
            }

            // Werte dürfen nur disposed werden, wenn sie dafür die "Freigabe" haben. Des Weiteren wurde ein weiterer Check eingebaut, welcher prüft, ob der Wert nicht schon bereits disposed ist
            // oder sich bereits im disposing-Prozess befindet.
            if (Utilities.isFunction(value.dispose) && value.isDisposable === true && value.dispose != null && !value.isDisposed && !value.__isDisposing) {
                value.dispose();
            }
            return;
        }

        if (value.isDisposable === true && value.dispose != null && !value.isDisposed && !value.__isDisposing) {
            value.dispose();
            return;
        }

        if (value.destroy != null && value.element != null) {
            try {
                value.destroy();
            }
            catch (e) {
                if(DEBUG_MODE)
                    console.debug("Error while Disposing Object Value: ", JSON.stringify(value), " Error: ", e.message);
            }
            return;
        }

        if (value instanceof jQuery || value instanceof HTMLElement) {
            if (value instanceof HTMLElement) {
                ko.cleanNode(value);
            }
            else {
                (value as JQuery).each((index: number, elem: HTMLElement) => {
                        ko.cleanNode(elem);
                    });

                    (value as JQuery).remove();
                }
            return;
        }

        if (disposePlain !== false && jQuery.isPlainObject(value)) {
            // Marker, um mögliche Endlosschleifen zu verhindern
            if (value[IS_DISPOSING]) {
                return;
            }

            value[IS_DISPOSING] = true;

            for (const key of Object.keys(value)) {
                this.disposeObject(value[key]);
            }

            // Entfernen wir explizit, um mögliche Seiteneffekte zu verhindern (Objekt wird nach dispose z. B. im localStorage gehalten)
            delete value[IS_DISPOSING];
            return;
        }
    }

    /**
     * Entfernt ein Property aus der Instanz
     */
    private removeProperty(property: string): void {
        if (this.blacklist.indexOf(property) === -1) {
            let propertyDescriptor = Object.getOwnPropertyDescriptor(this, property);

            // Überprüfen, ob Getter und Setter genutzt wurden und nur ein Getter vorhanden ist
            if (propertyDescriptor && propertyDescriptor.get && !propertyDescriptor.set) {
                delete this[property];
            }
            // ansonsten: Überprüfen, ob das Property gesetzt werden kann
            else if (propertyDescriptor && !propertyDescriptor.writable) {
                // Wenn nicht, muss das Property kurzzeitig auf schreibbar gestellt werden
                propertyDescriptor.writable = true;
                this[property] = null;
                // Wieder zurücksetzen
                propertyDescriptor.writable = false;
            }
            // Standardfall
            else if (propertyDescriptor) {
                this[property] = null;
            }
        }
    }

    /**
     * Hebt alle bisher registrierten Subscriptions wieder auf.
     */
    public disposeSubscriptions(): void {
        if (this.subscriptionCollection == null) {
            return;
        }

        for (let i = 0; i < this.subscriptionCollection.length; i++) {
            let subscription = this.subscriptionCollection[i];
            if (subscription["_state"] && subscription["_state"].isSleeping) {
                subscription["_state"].isSleeping = false;
            }
            if (Utilities.isFunction(subscription.dispose))
                subscription.dispose();
        }
        this.subscriptionCollection = [];
    }
    
    /**
     * Führt das Disposing asynchron aus.
     * Dies kann genutzt werden, um nach dem Disposing andere Logiken auszuführen.
     * Gibt zurück, ob das Objekt disposed ist.
     */
    public disposeAsync(): Promise<boolean> {
        if (this.isDisposedOrDisposing())
            return Promise.resolve(this.isDisposed);
        
        // Der currentPromiseBatcher darf nur für eine Anfrage genutzt werden,
        // daher führen wir diesen Call erst aus, wenn der vorherige Call durchgeführt wurde. 
        if (Class.currentPromiseBatcher != null) {
            return Class.currentPromiseBatcher
                .getPromise()
                .then(() => this.disposeAsync());
        }

        Class.currentPromiseBatcher = new PromiseBatcher<boolean>(
            () => {
                this._disposeAsync();
                return this.isDisposed;
            },
            () => {
                Class.currentPromiseBatcher = null;
            });
        
        return Class.currentPromiseBatcher.getPromise();
    }
            
    /**
     * Generelles Disposing welches ja nach DisposeBehavior synchron oder asynchron durchgeführt wird. 
     * Diese Methode sollte nicht überschrieben werden, da sie DisposeBehavior behandelt.
     * Für eigene Dispose-Logik soll bitte disposeInternal überschrieben werden. 
     */
    public dispose(): void {
        if (this.isDisposedOrDisposing())
            return;
        
        const behavior = this.getDisposeBehavior();
        switch (behavior) {
            case "Synchronous":
                // Wenn man die Zeiten für das Disposing messen will, kann man diese Codezeile einkommentieren
                // und die andere entsprechend auskommentieren.
                //this.logTime('Dispose', () => this._dispose(), 20);
                this._dispose(true);
                break;
            case "Asynchronous":
                this._disposeAsync();
                break;
            case "AsynchronousBatched":
                CallbackBatcher.add(() => this._dispose(true), Class.currentPromiseBatcher);
                break;
            default: 
                throw new Error("Unknown dispose behavior: " + behavior);
        }
    }

    private _disposeAsync(): void {
        if (this.isDisposedOrDisposing())
            return;
        
        this.__isDisposing = true;
        
        const action = (callback?: Function) => {
            window.setTimeout(() => {
                this._dispose(false);
                if (callback)
                    callback();
            })
        };
        
        if (Class.currentPromiseBatcher) {
            const promise = new Promise<void>((resolve) => {
                action(() => resolve());
            });

            Class.currentPromiseBatcher.add(promise);
        }
        else {
            action();
        }
    }
    
    private _dispose(checkDisposing: boolean): void {
        if (checkDisposing && this.isDisposedOrDisposing())
            return;
        
        this.__isDisposing = true;
        
        this.disposeInternal();
        
        this.isDisposed = true;
        this.__isDisposing = false;

        if (RADApplication.getInstance().profiler != null) {
            RADApplication.getInstance().profiler.onInstanceDisposed(this);
        }
    }

    protected disposeInternal(): void {
        this.unregisterAllMessageIds();
        this.disposeSubscriptions();

        if (this.disposeProperties !== false) {
            for (const property of Object.keys(this)) {
                if ((this.disposeProperties === true || (this.disposeProperties as any).indexOf(property) > -1)
                    && this.undisposeProperties.indexOf(property) === -1
                    && !property.startsWith("__")) {
                    this.disposeObject(this[property], false);
                }

                this.removeProperty(property);
            }
        }
    }
        
    public isDisposedOrDisposing(): boolean{
        return this.isDisposed || this.__isDisposing;
    }

    /**
     * Gibt an, ob das Disposing synchron, asynchron oder asynchron batched ausgeführt werden soll.
     * AsynchronousBatched sollte nur verwendet werden, wenn es sich um viele sehr kleine Instanzen (Link oder Image) handelt.
     */
    protected getDisposeBehavior(): DisposeBehavior {
        return "Synchronous";
    }

    /**
     * Fügt Objekte zur Instanz hinzu, welche beim Disposing ebenfalls disposed werden sollen.
     */
    public addDisposables(...disposables: Class[]): void {
        this.disposables.push(...disposables);
    }
    
    protected logTime(name: string, callback: Function, maxInMs: number = 30) : void{
        let id = name + ' # ' + this.constructor.name;
        const start = performance.now();

        callback();

        const elapsed = performance.now() - start;
        if (elapsed > maxInMs) {
            console.log(`${id} : ${elapsed.toFixed(2)} ms`);
        }
    }
}